import { Link, NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import { UserMenu } from "./UserMenu";
import { Breadcrumbs } from "./Breadcrumbs";
import { CardsIcon } from "./CardsIcon";
import { Footer } from "./Footer";
import { LoadingBar } from "./LoadingBar";
import { Logo } from "./Logo";
import { Notifications } from "./Notifications";

function IconHome({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 12l9-9 9 9" />
      <path d="M9 21V12h6v9" />
    </svg>
  );
}

function IconCards({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <rect
        x="3"
        y="4"
        width="12"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth={2}
        transform="rotate(-6 9 12)"
      />
      <rect
        x="9"
        y="4"
        width="12"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth={2}
        transform="rotate(6 15 12)"
      />
    </svg>
  );
}

function IconTrophy({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 9H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3" />
      <path d="M18 9h3a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-3" />
      <path d="M6 4h12v5a6 6 0 0 1-12 0V4z" />
      <path d="M12 15v3" />
      <path d="M8 21h8" />
    </svg>
  );
}

function IconMedal({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="14" r="6" />
      <path d="M8.21 1.6L7 4l-3 .5 2 2-.5 3L8 8l2.5 1.5" />
      <path d="M15.79 1.6L17 4l3 .5-2 2 .5 3L16 8l-2.5 1.5" />
    </svg>
  );
}

function IconBook({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}

const navItems = [
  { to: "/home", label: "Accueil", icon: IconHome },
  { to: "/games", label: "Parties", icon: IconCards },
  { to: "/tournaments", label: "Tournois", icon: IconTrophy },
  { to: "/leaderboard", label: "Leaderboard", icon: IconMedal },
  { to: "/rules", label: "Règles", icon: IconBook },
];

export function AppShell() {
  const { user } = useAuth();

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <LoadingBar />
      <Notifications />
      {/* Top header - minimal on mobile, full on desktop */}
      <header className="border-b border-gray-100 px-4 py-3 sm:px-6">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <CardsIcon className="h-6 w-6" color="#FF5100" />
            <Logo className="h-6" color="#FF5100" />
          </Link>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Desktop nav */}
      <nav className="hidden border-b border-gray-100 sm:block">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="-ml-4 flex gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `border-b-2 px-4 py-2.5 text-sm font-medium transition ${
                    isActive
                      ? "border-ink text-ink"
                      : "border-transparent text-smoke hover:text-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `border-b-2 px-4 py-2.5 text-sm font-medium transition ${
                    isActive
                      ? "border-ember text-ember"
                      : "border-transparent text-ember hover:text-ember/80"
                  }`
                }
              >
                Admin
              </NavLink>
            )}
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="flex-1 px-4 py-4 pb-20 sm:px-6 sm:py-6 sm:pb-6">
        <div className="mx-auto max-w-3xl">
          <Breadcrumbs />
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <Footer className="hidden border-t border-gray-100 sm:block" />

      {/* Bottom tab bar - mobile only */}
      <nav className="fixed bottom-0 left-0 right-0 border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)] sm:hidden">
        <div className="flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition ${
                  isActive ? "text-ember" : "text-gray-400"
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
